import { useState } from 'react';
import { Plus, Pencil, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';
import Button from '@/components/ui/Button';
import { transactionsApi } from '@/api';
import TransactionFormModal from './TransactionFormModal';

const formatEuros = (value) => {
  if (value == null) return '-';
  return Number(value).toLocaleString('fr-FR') + ' €';
};

export default function TransactionTable({
  transactions,
  etudeId,
  onRefresh,
  onRefreshDvf,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const handleAdd = () => {
    setEditingTransaction(null);
    setModalOpen(true);
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    await transactionsApi.delete(id);
    onRefresh();
  };

  const handleSave = async (payload) => {
    if (editingTransaction) {
      await transactionsApi.update(editingTransaction.id, payload);
    } else {
      await transactionsApi.create({ ...payload, etude_marche_id: etudeId });
    }
    setModalOpen(false);
    onRefresh();
  };

  const handleToggleExclue = async (id) => {
    setTogglingId(id);
    try {
      await transactionsApi.toggleExclue(id);
      onRefresh();
    } catch (err) {
      console.error('Toggle exclue failed:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const activeCount = transactions.filter((t) => !t.exclue).length;
  const excludedCount = transactions.filter((t) => t.exclue).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">
          Transactions comparables
          {transactions.length > 0 && (
            <span className="ml-2 text-sm font-normal text-slate-500">
              {activeCount} retenue{activeCount > 1 ? 's' : ''}
              {excludedCount > 0 && ` (${excludedCount} exclue${excludedCount > 1 ? 's' : ''})`}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {onRefreshDvf && (
            <Button size="sm" variant="secondary" onClick={onRefreshDvf}>
              <RefreshCw className="h-4 w-4" />
              Rafraîchir DVF
            </Button>
          )}
          {etudeId && (
            <Button size="sm" onClick={handleAdd}>
              <Plus className="h-4 w-4" />
              Ajouter
            </Button>
          )}
        </div>
      </div>

      {transactions.length === 0 ? (
        <p className="text-sm text-slate-500 italic">Aucune transaction comparable.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-slate-200 rounded-lg">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-4 py-2 text-left font-medium text-slate-700">Source</th>
                <th className="px-4 py-2 text-left font-medium text-slate-700">Adresse</th>
                <th className="px-4 py-2 text-right font-medium text-slate-700">Surface</th>
                <th className="px-4 py-2 text-right font-medium text-slate-700">Prix</th>
                <th className="px-4 py-2 text-right font-medium text-slate-700">€/m²</th>
                <th className="px-4 py-2 text-left font-medium text-slate-700">Date</th>
                <th className="px-4 py-2 text-right font-medium text-slate-700">Distance</th>
                <th className="px-4 py-2 text-center font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr
                  key={t.id}
                  className={`border-t border-slate-200 hover:bg-slate-50 ${
                    t.exclue ? 'opacity-40 line-through' : ''
                  }`}
                >
                  <td className="px-4 py-2 text-slate-800">{t.source ?? '-'}</td>
                  <td className="px-4 py-2 text-slate-800 truncate max-w-xs" title={t.adresse}>
                    {t.adresse ?? '-'}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800">
                    {t.surface_habitable_m2 != null ? `${t.surface_habitable_m2} m²` : '-'}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-800">{formatEuros(t.prix_euros)}</td>
                  <td className="px-4 py-2 text-right text-slate-800">{formatEuros(t.prix_unitaire_euros_m2)}</td>
                  <td className="px-4 py-2 text-slate-800">{t.date_transaction ?? '-'}</td>
                  <td className="px-4 py-2 text-right text-slate-800">
                    {t.distance_m != null ? `${Math.round(t.distance_m)} m` : '-'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        className={`p-1.5 rounded ${
                          t.exclue
                            ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                        }`}
                        onClick={() => handleToggleExclue(t.id)}
                        disabled={togglingId === t.id}
                        title={t.exclue ? 'Inclure dans l\u2019analyse' : 'Exclure de l\u2019analyse'}
                      >
                        {t.exclue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        className="p-1.5 text-slate-500 hover:text-carex-600 hover:bg-slate-100 rounded"
                        onClick={() => handleEdit(t)}
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                        onClick={() => handleDelete(t.id)}
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <TransactionFormModal
          transaction={editingTransaction}
          onSave={handleSave}
          onCancel={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
